// Persistent take labelling for STS takes. The label is `Take {s}-{n}`,
// where `s` is the recording-session number and `n` is the take's ordinal
// within that session. Both are stamped on the `STSTake` at creation time
// (see RecordingStudio + sessions store) and carried for the take's
// lifetime — reordering, copy-to-master, or trimming never renumber it.
//
// `Take {s}-{n}` reads naturally as "the n-th take in session s", so a
// user looking at `Take 2-3` knows it was the third take they recorded
// during their second recording session on this asset.
//
// `fallbackIdx` (1-based) is used only for legacy takes recorded before
// the persistent fields were introduced — they fall back to the old
// `Take {idx}` shape so old sessions in IndexedDB still render sensibly.

import type { STSTake } from '@shared/types';

/** Build the user-facing label for a take.
 *
 *  @param take          the take object (may be a persisted STSTake or a
 *                       DraftTake — the only fields read are `session` and
 *                       `takeInSession`).
 *  @param fallbackIdx0  zero-based index in the take list; converted to
 *                       1-based for the legacy `Take N` fallback. */
export function formatTakeLabel(
  take: Pick<STSTake, 'session' | 'takeInSession'>,
  fallbackIdx0: number,
): string {
  if (
    typeof take.session === 'number' &&
    typeof take.takeInSession === 'number'
  ) {
    return `Take ${take.session}-${take.takeInSession}`;
  }
  return `Take ${fallbackIdx0 + 1}`;
}
